import { useQuery } from "@tanstack/react-query";
import { Check, CheckCheck, Copy, Download, Terminal } from "lucide-react";
import { useState } from "react";

import { fetchCliRelease } from "@/lib/api";
import type { CliReleaseTarget } from "@/lib/types";

const TARGETS: Array<{
  id: CliReleaseTarget;
  platform: string;
  architecture: string;
}> = [
  { id: "darwin-arm64", platform: "macOS", architecture: "Apple silicon" },
  { id: "darwin-x64", platform: "macOS", architecture: "Intel" },
  { id: "linux-x64", platform: "Linux", architecture: "x64" },
  { id: "linux-arm64", platform: "Linux", architecture: "ARM64" },
  { id: "windows-x64", platform: "Windows", architecture: "x64" },
];

export const CLI_INSTALL_COMMAND = "curl -fsSL https://sherlog.bold.org/install | bash";
export const WINDOWS_INSTALL_COMMAND = "irm https://sherlog.bold.org/install.ps1 | iex";
export const SKILLS_INSTALL_COMMAND = "sherlog skills install";
export const SKILLS_REMOVE_COMMAND = `for root in "$HOME/.agents/skills" "$HOME/.claude/skills"; do
  for link in "$root"/*; do
    [ -L "$link" ] || continue
    case "$(readlink "$link")" in
      "$HOME/.sherlog/skills/"*) rm -- "$link" ;;
    esac
  done
done

rm -rf -- "$HOME/.sherlog/skills"`;

export function DownloadView() {
  const release = useQuery({ queryKey: ["cli-release"], queryFn: fetchCliRelease });
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  async function copyInstallCommand(command: string) {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(command);
      window.setTimeout(() => setCopiedCommand(null), 2000);
    } catch {
      setCopiedCommand(null);
    }
  }

  return (
    <div className="bg-cyber-bg text-cyber-text h-full overflow-y-auto px-5 py-8 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <div className="border-cyber-green/30 bg-cyber-green/10 text-cyber-green inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs tracking-[0.2em] uppercase">
            <Terminal className="h-3.5 w-3.5" />
            Sherlog CLI
          </div>
          <div className="border-cyber-border bg-cyber-surface text-cyber-muted rounded-full border px-3 py-1 text-xs">
            {release.isPending
              ? "Checking latest version..."
              : release.isError
                ? "Latest version unavailable"
                : `Latest version: ${release.data.version}`}
          </div>
        </div>
        <h1 className="max-w-3xl text-4xl leading-tight font-bold sm:text-6xl">
          Use Sherlog from your favorite harness or AI agent.
        </h1>
        <p className="text-cyber-muted mt-5 max-w-2xl text-base leading-7 sm:text-lg">
          Install Sherlog with one command on macOS, Linux, or Windows, ready for the tools you
          already use.
        </p>

        <section className="border-cyber-green/40 bg-cyber-surface mt-10 rounded-2xl border p-5 sm:p-7">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Install Sherlog</h2>
              <p className="text-cyber-muted mt-1 text-sm">Run this command to get started.</p>
            </div>
            <span className="bg-cyber-green/10 text-cyber-green hidden rounded-full px-3 py-1 text-xs sm:block">
              macOS + Linux + Windows
            </span>
          </div>
          <div className="space-y-3">
            {[
              { label: "macOS and Linux", command: CLI_INSTALL_COMMAND },
              { label: "Windows PowerShell", command: WINDOWS_INSTALL_COMMAND },
            ].map(({ label, command }) => (
              <div key={label} className="border-cyber-border bg-cyber-bg rounded-xl border p-3">
                <div className="text-cyber-muted px-2 pb-1 text-xs">{label}</div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <code className="text-cyber-text min-w-0 flex-1 overflow-x-auto px-2 py-2 text-sm whitespace-nowrap sm:text-base">
                    {command}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyInstallCommand(command)}
                    className="bg-cyber-green text-cyber-bg inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold transition-opacity hover:opacity-90"
                  >
                    {copiedCommand === command ? (
                      <CheckCheck className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copiedCommand === command ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-cyber-border bg-cyber-surface my-8 grid gap-4 rounded-2xl border p-6 md:grid-cols-3">
          {[
            ["1", "Install", "Paste the command above."],
            ["2", "Sign in", "Run sherlog login. Your browser will open to confirm your account."],
            ["3", "Start", "Run sherlog --help to see what Sherlog can do."],
          ].map(([number, title, description]) => (
            <div key={number} className="flex gap-4">
              <div className="border-cyber-green/40 text-cyber-green flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm">
                {number}
              </div>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-cyber-muted mt-2 text-sm leading-6">{description}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="border-cyber-cyan/40 bg-cyber-surface mb-4 rounded-2xl border p-5 sm:p-7">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold">Install Sherlog skills</h2>
            <p className="text-cyber-muted mt-2 text-sm leading-6">
              Add Sherlog&apos;s portable skills to OpenCode, Claude Code, Codex, and Cursor.
              Existing skills are preserved, and Sherlog refuses to overwrite conflicting or locally
              modified skills.
            </p>
          </div>
          <div className="border-cyber-border bg-cyber-bg mt-4 rounded-xl border p-3">
            <code className="text-cyber-text block overflow-x-auto px-2 py-2 text-sm whitespace-nowrap sm:text-base">
              {SKILLS_INSTALL_COMMAND}
            </code>
          </div>
          <p className="text-cyber-muted mt-3 text-xs leading-5">
            Check the installation with <code>sherlog skills status</code>, then restart your agent
            so it reloads its skill catalog.
          </p>
        </section>

        <details className="border-cyber-border bg-cyber-surface mb-4 rounded-2xl border p-5">
          <summary className="cursor-pointer font-semibold">
            How do I remove the Sherlog skills?
          </summary>
          <p className="text-cyber-muted mt-3 text-sm leading-6">
            Run this in Bash. It removes only links targeting Sherlog-managed skills, then removes
            their managed copies. Other global skills and your Sherlog login remain untouched.
          </p>
          <pre className="border-cyber-border bg-cyber-bg text-cyber-text mt-4 overflow-x-auto rounded-xl border p-4 text-xs leading-5 sm:text-sm">
            <code>{SKILLS_REMOVE_COMMAND}</code>
          </pre>
          <p className="text-cyber-muted mt-3 text-xs leading-5">
            Confirm removal with <code>sherlog skills status</code>.
          </p>
        </details>

        <details className="border-cyber-border bg-cyber-surface mb-4 rounded-2xl border p-5">
          <summary className="cursor-pointer font-semibold">
            Moving from the local Sherlog repository?
          </summary>
          <p className="text-cyber-muted mt-3 text-sm leading-6">
            The repository is no longer needed for everyday Sherlog use. Keep it only if you develop
            Sherlog itself.
          </p>
          <ol className="text-cyber-muted mt-4 space-y-3 text-sm leading-6">
            <li>
              <strong className="text-cyber-text">1. Remove local overrides.</strong> Delete{" "}
              <code>SHERLOG_EXECUTION_PROFILE=local</code> from your shell setup and stop sourcing
              the repository&apos;s <code>.env.local</code>. Logging in does not remove shell
              variables.
            </li>
            <li>
              <strong className="text-cyber-text">2. Retire the repository command.</strong> Stop
              running <code>sync:env</code> and remove old aliases or links to the repository.{" "}
              <code>command -v sherlog</code> should point to <code>~/.local/bin/sherlog</code>.
            </li>
            <li>
              <strong className="text-cyber-text">3. Start with a fresh sign-in.</strong> Run{" "}
              <code>sherlog login --force</code>, then confirm it with <code>sherlog status</code>.
            </li>
          </ol>
          <div className="border-cyber-cyan/30 bg-cyber-cyan/5 text-cyber-muted mt-4 rounded-xl border p-4 text-sm leading-6">
            HubSpot and Intercom now connect through their official MCPs. Configure those in your
            preferred harness or agent; Sherlog skill installation will be a separate step.
          </div>
        </details>

        <details className="border-cyber-border bg-cyber-surface mb-8 rounded-2xl border p-5">
          <summary className="cursor-pointer font-semibold">Manual downloads</summary>
          <p className="text-cyber-muted mt-3 text-sm">
            Use these only if the install command does not work for you.
          </p>
          {release.isPending ? (
            <div className="text-cyber-muted py-8 text-center">Loading downloads...</div>
          ) : release.isError ? (
            <div className="border-cyber-red/40 bg-cyber-red/10 text-cyber-red mt-5 rounded-xl border p-5">
              Downloads are temporarily unavailable. Please try again shortly.
            </div>
          ) : (
            <>
              <p className="text-cyber-muted mt-2 text-xs">
                Latest version: {release.data.version}
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {TARGETS.map((target) => {
                  return (
                    <a
                      key={target.id}
                      href={`/v1/cli/releases/${encodeURIComponent(release.data.version)}/assets/${target.id}`}
                      className="border-cyber-border bg-cyber-bg hover:border-cyber-cyan/60 flex items-center justify-between gap-3 rounded-xl border p-4 transition-colors"
                    >
                      <div>
                        <div className="font-semibold">{target.platform}</div>
                        <div className="text-cyber-muted mt-1 text-xs">{target.architecture}</div>
                      </div>
                      <Download className="text-cyber-cyan h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </>
          )}
        </details>

        <div className="text-cyber-muted mb-10 flex items-center gap-2 text-sm">
          <Check className="text-cyber-green h-4 w-4" />
          On macOS and Linux, run <code>sherlog update</code>. On Windows, rerun the PowerShell
          installer.
        </div>
      </div>
    </div>
  );
}
